import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';


const inputStyle = {
  display: 'flex',
  width: '100%',
  borderRadius: '0.375rem',
  border: '1px solid rgba(255,255,255,0.4)',
  backgroundColor: 'rgba(255,255,255,0.1)',
  color: 'white',
  outline: 'none',
  height: 'clamp(40px, 5.5vw, 80px)',
  padding: '0 clamp(10px, 1.5vw, 22px)',
  fontSize: 'clamp(13px, 1.6vw, 26px)',
};

const labelStyle = {
  display: 'block',
  fontWeight: '500',
  color: 'white',
  fontSize: 'clamp(12px, 1.5vw, 24px)',
  marginBottom: 'clamp(4px, 0.6vw, 10px)',
};

const helperStyle = {
  color: 'rgba(255,255,255,0.7)',
  fontSize: 'clamp(10px, 1.2vw, 20px)',
  marginTop: 'clamp(3px, 0.4vw, 7px)',
};

const errorStyle = {
  color: '#fca5a5',
  fontSize: 'clamp(10px, 1.2vw, 20px)',
  marginTop: 'clamp(3px, 0.4vw, 7px)',
};

const fieldGap = { marginBottom: 'clamp(12px, 2vw, 32px)' };

const RegistrationForm = ({ onSubmit, onSkip, allowSkip = true }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    specialty: '',
    institution: '',
    consentData: false,
    consentMarketing: false
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex?.test(email);
  };

  const calculateProgress = () => {
    const fields = ['firstName', 'lastName', 'phone', 'email', 'specialty', 'consentData'];
    const completed = fields?.filter(field => {
      if (field === 'consentData') return formData?.[field];
      return formData?.[field]?.trim() !== '';
    })?.length;
    return Math.round((completed / fields?.length) * 100);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData?.firstName?.trim()) newErrors.firstName = 'El nombre es obligatorio';
    if (!formData?.lastName?.trim()) newErrors.lastName = 'El apellido es obligatorio';
    if (!formData?.phone?.trim()) {
      newErrors.phone = 'El número telefónico es obligatorio';
    } else if (!/^[\d\s\+\-\(\)]{7,20}$/?.test(formData?.phone)) {
      newErrors.phone = 'Por favor, introduce un número telefónico válido';
    }
    if (!formData?.email?.trim()) {
      newErrors.email = 'El correo electrónico es obligatorio';
    } else if (!validateEmail(formData?.email)) {
      newErrors.email = 'Por favor, introduce un correo electrónico válido';
    }
    if (!formData?.specialty) newErrors.specialty = 'Por favor, escribe tu especialidad médica';
    if (!formData?.consentData) newErrors.consentData = 'Debes aceptar la política de protección de datos para continuar';
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1500));
      onSubmit(formData);
    } catch (error) {
      setErrors({ submit: 'Error al enviar el formulario. Por favor, inténtalo de nuevo.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const progress = calculateProgress();

  return (
    <form onSubmit={handleSubmit} style={{ color: 'white' }}>
      {/* Progress Bar */}
      <div
        className="bg-white/10 rounded-lg border border-white/30"
        style={{ padding: 'clamp(10px, 1.5vw, 22px)', marginBottom: 'clamp(16px, 2.5vw, 36px)' }}
      >
        <div className="flex items-center justify-between" style={{ marginBottom: 'clamp(6px, 1vw, 14px)' }}>
          <span style={{ fontSize: 'clamp(12px, 1.5vw, 24px)', fontWeight: '500', color: 'white' }}>
            Progreso del registro
          </span>
          <span style={{ fontSize: 'clamp(12px, 1.5vw, 24px)', fontWeight: '600', color: 'white' }}>
            {progress}%
          </span>
        </div>
        <div
          className="w-full bg-white/20 rounded-full overflow-hidden"
          style={{ height: 'clamp(6px, 1vw, 14px)' }}
        >
          <div
            className="h-full bg-white transition-all duration-500 ease-out"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Name Row */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))',
          gap: 'clamp(12px, 2vw, 28px)',
          marginBottom: 'clamp(12px, 2vw, 32px)'
        }}
      >
        <div>
          <label style={labelStyle}>
            Nombre <span style={{ color: 'white', marginLeft: '4px' }}>*</span>
          </label>
          <input
            type="text"
            placeholder="Tu nombre"
            value={formData?.firstName}
            onChange={(e) => handleChange('firstName', e?.target?.value)}
            disabled={isSubmitting}
            style={inputStyle}
          />
          {errors?.firstName && <p style={errorStyle}>{errors?.firstName}</p>}
        </div>
        <div>
          <label style={labelStyle}>
            Apellido <span style={{ color: 'white', marginLeft: '4px' }}>*</span>
          </label>
          <input
            type="text"
            placeholder="Tu apellido"
            value={formData?.lastName}
            onChange={(e) => handleChange('lastName', e?.target?.value)}
            disabled={isSubmitting}
            style={inputStyle}
          />
          {errors?.lastName && <p style={errorStyle}>{errors?.lastName}</p>}
        </div>
      </div>

      {/* Phone */}
      <div style={fieldGap}>
        <label style={labelStyle}>
          Número telefónico <span style={{ color: 'white', marginLeft: '4px' }}>*</span>
        </label>
        <input
          type="tel"
          placeholder="+34 600 000 000"
          value={formData?.phone}
          onChange={(e) => handleChange('phone', e?.target?.value)}
          disabled={isSubmitting}
          style={inputStyle}
        />
        {!errors?.phone && <p style={helperStyle}>Incluye el código de país si aplica</p>}
        {errors?.phone && <p style={errorStyle}>{errors?.phone}</p>}
      </div>

      {/* Email */}
      <div style={fieldGap}>
        <label style={labelStyle}>
          Correo electrónico <span style={{ color: 'white', marginLeft: '4px' }}>*</span>
        </label>
        <input
          type="email"
          placeholder="tu.email@ejemplo.com"
          value={formData?.email}
          onChange={(e) => handleChange('email', e?.target?.value)}
          disabled={isSubmitting}
          style={inputStyle}
        />
        {!errors?.email && <p style={helperStyle}>Utilizaremos tu correo para enviarte información sobre premios y eventos</p>}
        {errors?.email && <p style={errorStyle}>{errors?.email}</p>}
      </div>

      {/* Specialty */}
      <div style={fieldGap}>
        <label style={labelStyle}>
          Especialidad médica <span style={{ color: 'white', marginLeft: '4px' }}>*</span>
        </label>
        <input
          type="text"
          placeholder="Escribe tu especialidad médica"
          value={formData?.specialty}
          onChange={(e) => handleChange('specialty', e?.target?.value)}
          disabled={isSubmitting}
          style={inputStyle}
        />
        {!errors?.specialty && <p style={helperStyle}>Escribe tu área de especialización</p>}
        {errors?.specialty && <p style={errorStyle}>{errors?.specialty}</p>}
      </div>

      {/* Institution */}
      <div style={fieldGap}>
        <label style={labelStyle}>Institución (opcional)</label>
        <input
          type="text"
          placeholder="Hospital o centro médico"
          value={formData?.institution}
          onChange={(e) => handleChange('institution', e?.target?.value)}
          disabled={isSubmitting}
          style={inputStyle}
        />
        <p style={helperStyle}>Nombre de tu institución o centro de trabajo</p>
      </div>

      {/* Checkboxes */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'clamp(12px, 2vw, 28px)', paddingTop: 'clamp(6px, 1vw, 14px)', marginBottom: 'clamp(16px, 2.5vw, 36px)' }}>
        {/* Consent Data */}
        <div className="flex items-start" style={{ gap: 'clamp(10px, 1.5vw, 20px)' }}>
          <input
            type="checkbox"
            id="consentData"
            checked={formData?.consentData}
            onChange={(e) => handleChange('consentData', e?.target?.checked)}
            disabled={isSubmitting}
            style={{
              marginTop: '2px',
              width: 'clamp(16px, 2.2vw, 32px)',
              height: 'clamp(16px, 2.2vw, 32px)',
              flexShrink: 0,
              cursor: 'pointer',
              accentColor: '#2dd4bf'
            }}
          />
          <div style={{ flex: 1 }}>
            <label
              htmlFor="consentData"
              style={{ ...labelStyle, marginBottom: 'clamp(2px, 0.4vw, 6px)', cursor: 'pointer' }}
            >
              Acepto la política de protección de datos <span style={{ color: 'white', marginLeft: '4px' }}>*</span>
            </label>
            {!errors?.consentData && (
              <p style={helperStyle}>He leído y acepto el tratamiento de mis datos personales según la normativa GDPR</p>
            )}
            {errors?.consentData && <p style={errorStyle}>{errors?.consentData}</p>}
          </div>
        </div>

        {/* Consent Marketing */}
        <div className="flex items-start" style={{ gap: 'clamp(10px, 1.5vw, 20px)' }}>
          <input
            type="checkbox"
            id="consentMarketing"
            checked={formData?.consentMarketing}
            onChange={(e) => handleChange('consentMarketing', e?.target?.checked)}
            disabled={isSubmitting}
            style={{
              marginTop: '2px',
              width: 'clamp(16px, 2.2vw, 32px)',
              height: 'clamp(16px, 2.2vw, 32px)',
              flexShrink: 0,
              cursor: 'pointer',
              accentColor: '#2dd4bf'
            }}
          />
          <div style={{ flex: 1 }}>
            <label
              htmlFor="consentMarketing"
              style={{ ...labelStyle, marginBottom: 'clamp(2px, 0.4vw, 6px)', cursor: 'pointer' }}
            >
              Deseo recibir información comercial de XAIKUS
            </label>
            <p style={helperStyle}>Acepto recibir comunicaciones sobre productos, eventos y novedades médicas</p>
          </div>
        </div>
      </div>

      {errors?.submit && (
        <div
          className="flex items-center bg-red-500/20 border border-red-400/30 rounded-lg"
          style={{ gap: 'clamp(8px, 1vw, 14px)', padding: 'clamp(10px, 1.5vw, 20px)', marginBottom: 'clamp(12px, 2vw, 24px)' }}
        >
          <Icon name="AlertCircle" size={Math.max(14, Math.round(window.innerWidth * 0.018))} color="white" />
          <p style={{ color: 'white', fontSize: 'clamp(12px, 1.4vw, 22px)' }}>{errors?.submit}</p>
        </div>
      )}

      {/* Action Buttons */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'clamp(10px, 1.5vw, 22px)',
          paddingTop: 'clamp(6px, 1vw, 14px)'
        }}
      >
        {allowSkip && (
          <button
            type="button"
            onClick={onSkip}
            disabled={isSubmitting}
            className="w-full rounded-lg border border-white/40 bg-white/10 text-white font-medium hover:bg-white/20 active:bg-white/30 transition-all touch-manipulation flex items-center justify-center"
            style={{
              height: 'clamp(44px, 6vw, 88px)',
              fontSize: 'clamp(13px, 1.6vw, 26px)',
              gap: 'clamp(6px, 1vw, 14px)'
            }}
          >
            Participar sin registro
            <Icon name="ArrowRight" size={Math.max(14, Math.round(window.innerWidth * 0.018))} />
          </button>
        )}
        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full rounded-lg text-white font-semibold hover:opacity-90 active:opacity-80 transition-all touch-manipulation flex items-center justify-center"
          style={{
            height: 'clamp(44px, 6vw, 88px)',
            fontSize: 'clamp(13px, 1.6vw, 26px)',
            gap: 'clamp(6px, 1vw, 14px)',
            background: 'linear-gradient(135deg, #2dd4bf 0%, #0d9488 100%)',
            opacity: isSubmitting ? 0.7 : 1
          }}
        >
          {isSubmitting ? (
            <>
              <svg className="animate-spin" style={{ width: 'clamp(16px, 2vw, 28px)', height: 'clamp(16px, 2vw, 28px)' }} fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              Enviando...
            </>
          ) : (
            <>
              <Icon name="CheckCircle2" size={Math.max(14, Math.round(window.innerWidth * 0.018))} />
              Completar registro
            </>
          )}
        </button>
      </div>
    </form>
  );
};

export default RegistrationForm;